/**
 * Cycle Design — Collections
 *
 * Two-column editor for item collections used during cycle design.
 * Left panel:  list of all collections (select active).
 * Right panel: active collection details with inline item management.
 *
 * Mirrors the ItemPoolEditor layout so Cycle Design feels cohesive.
 */

import { useState, useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  FolderOpen,
  Plus,
  Trash2,
  Pencil,
  X,
  Check,
  Search,
  ExternalLink,
  Package,
  Ban,
  ShieldAlert,
  AlertTriangle,
  CheckCircle2,
  GitBranch,
  ChevronDown,
  ChevronUp,
  BarChart3,
} from 'lucide-react';
import { PageHeader } from '../../components/prioritization/PageHeader';
import { useCollectionsStore } from '../../stores/prioritization/collectionsStore';
import { useItemsStore } from '../../stores/prioritization/itemsStore';
import { useUIStore } from '../../stores/prioritization/uiStore';
import { useItemRelationshipsStore } from '../../stores/prioritization/itemRelationshipsStore';
import type { Collection } from '../../types/prioritization/collections';
import type { Item } from '../../types/prioritization/lifecycle';
import { evaluateItemRelationships, type RelationshipEvalContext, type RuleOutcome, type RuleOutcomeLevel } from '../../lib/itemRelationshipRuleEngine';

// ============================================================================
// Create Collection Modal
// ============================================================================

function CreateCollectionModal({
  isOpen,
  onClose,
  onCreate,
}: {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (name: string, description?: string) => void;
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  if (!isOpen) return null;

  const handleCreate = () => {
    if (!name.trim()) return;
    onCreate(name.trim(), description.trim() || undefined);
    setName('');
    setDescription('');
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-[var(--bg-raised)] shadow-xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-sm font-semibold text-[var(--text-pri)] mb-4">New Collection</h2>
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-[var(--text-sec)] mb-1">Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Q3 Customer Asks"
              className="w-full px-3 py-2 text-sm border border-[var(--rule)] focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              autoFocus
              onKeyDown={(e) => { if (e.key === 'Enter') handleCreate(); if (e.key === 'Escape') onClose(); }}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--text-sec)] mb-1">Description (optional)</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What ties these items together?"
              rows={2}
              className="w-full px-3 py-2 text-sm border border-[var(--rule)] focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button
            onClick={onClose}
            className="px-3 py-2 text-sm text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleCreate}
            disabled={!name.trim()}
            className="px-3 py-2 text-sm font-medium bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 transition-colors"
          >
            Create
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Rename inline input
// ============================================================================

function RenameInput({
  value,
  onSave,
  onCancel,
}: {
  value: string;
  onSave: (v: string) => void;
  onCancel: () => void;
}) {
  const [val, setVal] = useState(value);
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => { ref.current?.focus(); }, []);
  return (
    <div className="flex items-center gap-1.5 flex-1">
      <input
        ref={ref}
        type="text"
        value={val}
        onChange={(e) => setVal(e.target.value)}
        className="flex-1 px-2 py-1 text-sm border border-[var(--rule)] focus:ring-2 focus:ring-primary-500"
        onKeyDown={(e) => {
          if (e.key === 'Enter') onSave(val.trim());
          if (e.key === 'Escape') onCancel();
        }}
      />
      <button onClick={() => onSave(val.trim())} className="p-1 text-[var(--green)] dark:text-[var(--text-ter)] hover:text-[var(--green)] dark:hover:text-[var(--text-ter)]">
        <Check className="h-3.5 w-3.5" />
      </button>
      <button onClick={onCancel} className="p-1 text-[var(--text-ter)] hover:text-[var(--text-sec)]">
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

// ============================================================================
// Add Items Panel (search + add)
// ============================================================================

function AddItemsPanel({
  existingIds,
  onAdd,
  onClose,
}: {
  existingIds: string[];
  onAdd: (itemId: string) => void;
  onClose: () => void;
}) {
  const [query, setQuery] = useState('');
  const allItems = useItemsStore((s) => s.items);

  const results = useMemo(() => {
    const q = query.toLowerCase().trim();
    if (!q) return [];
    return allItems
      .filter((i) => !existingIds.includes(i.id))
      .filter((i) => i.title?.toLowerCase().includes(q) || i.description?.toLowerCase().includes(q))
      .slice(0, 10);
  }, [query, allItems, existingIds]);

  return (
    <div className="bg-[var(--bg-raised)] border border-[var(--rule)] overflow-hidden">
      <div className="px-4 py-3 border-b border-[var(--rule)] flex items-center justify-between">
        <h3 className="text-sm font-semibold text-[var(--text-pri)]">Add Items to Collection</h3>
        <button onClick={onClose} className="p-1 text-[var(--text-ter)] hover:text-[var(--text-sec)] rounded transition-colors">
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="p-3">
        <div className="relative mb-2">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[var(--text-ter)]" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search items by title or description…"
            className="w-full pl-8 pr-3 py-2 text-sm border border-[var(--rule)] focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            autoFocus
          />
        </div>
        {query.trim() && results.length === 0 && (
          <p className="text-xs text-[var(--text-ter)] px-1 py-2">No matching items outside this collection.</p>
        )}
        {results.length > 0 && (
          <div className="divide-y divide-[var(--rule)]">
            {results.map((item) => (
              <div key={item.id} className="flex items-center gap-3 py-2 px-1">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[var(--text-pri)] truncate">{item.title}</p>
                  {item.itemCategory && (
                    <span className="text-xs text-[var(--text-ter)] capitalize">{item.itemCategory}</span>
                  )}
                </div>
                <button
                  onClick={() => { onAdd(item.id); setQuery(''); }}
                  className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 transition-colors flex-shrink-0"
                >
                  <Plus className="h-3 w-3" /> Add
                </button>
              </div>
            ))}
          </div>
        )}
        {!query.trim() && (
          <p className="text-xs text-[var(--text-ter)]">Start typing to search for items to add.</p>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Scope level helpers
// ============================================================================

const SCOPE_LEVEL_ORDER: RuleOutcomeLevel[] = ['block', 'require_approval', 'warn', 'allow'];

function worstLevel(outcomes: RuleOutcome[]): RuleOutcomeLevel {
  if (outcomes.length === 0) return 'allow';
  return outcomes.reduce<RuleOutcomeLevel>((worst, o) => {
    return SCOPE_LEVEL_ORDER.indexOf(o.level) < SCOPE_LEVEL_ORDER.indexOf(worst) ? o.level : worst;
  }, 'allow');
}

const SCOPE_LEVEL_CHIP: Record<RuleOutcomeLevel, { label: string; icon: React.ElementType; cls: string }> = {
  block:            { label: 'Blocked',  icon: Ban,          cls: 'bg-[var(--coral-dim)] text-[var(--coral)]' },
  require_approval: { label: 'Review',   icon: ShieldAlert,  cls: 'bg-[var(--mustard-dim)] text-[var(--mustard)]' },
  warn:             { label: 'Warning',  icon: AlertTriangle,cls: 'bg-[var(--mustard-dim)] text-[var(--mustard)]' },
  allow:            { label: 'Clear',    icon: CheckCircle2, cls: 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]' },
};

// ============================================================================
// Scope Analyzer — per-item table
// ============================================================================

interface ItemScopeRow {
  item: Item;
  scopeOutcomes: RuleOutcome[];
  allOutcomes: RuleOutcome[];
}

function ScopeAnalyzerTable({
  rows,
  onRemoveItem,
}: {
  rows: ItemScopeRow[];
  onRemoveItem: (itemId: string) => void;
}) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'scope' | 'all'>('scope');

  const blockedCount = rows.filter((r) => worstLevel(r.scopeOutcomes) === 'block').length;
  const warnCount = rows.filter((r) => ['warn', 'require_approval'].includes(worstLevel(r.scopeOutcomes))).length;

  return (
    <div className="bg-[var(--bg-raised)] border border-[var(--rule)] overflow-hidden">
      {/* Table header */}
      <div className="px-4 py-3 border-b border-[var(--rule)] flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <BarChart3 className="h-4 w-4 text-[var(--text-ter)]" />
          <h2 className="text-sm font-semibold text-[var(--text-pri)]">
            Scope Analyzer
            <span className="ml-1.5 text-[var(--text-ter)] font-normal">({rows.length} items)</span>
          </h2>
        </div>
        <div className="flex items-center gap-3">
          {/* View toggle */}
          <div className="flex items-center border border-[var(--rule)] overflow-hidden text-xs">
            <button
              type="button"
              onClick={() => setActiveTab('scope')}
              className={`px-2.5 py-1.5 font-medium transition-colors ${
                activeTab === 'scope' ? 'bg-[var(--hover-tint)] text-[var(--text-pri)]' : 'text-[var(--text-ter)] hover:bg-[var(--surface)]'
              }`}
            >
              Scope gates
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('all')}
              className={`px-2.5 py-1.5 font-medium transition-colors border-l border-[var(--rule)] ${
                activeTab === 'all' ? 'bg-[var(--hover-tint)] text-[var(--text-pri)]' : 'text-[var(--text-ter)] hover:bg-[var(--surface)]'
              }`}
            >
              All gates
            </button>
          </div>
          {/* Counters */}
          {blockedCount > 0 && (
            <span className="inline-flex items-center gap-1 text-xs font-medium text-[var(--coral)]">
              <Ban className="h-3.5 w-3.5" />{blockedCount} blocked
            </span>
          )}
          {warnCount > 0 && (
            <span className="inline-flex items-center gap-1 text-xs font-medium text-[var(--mustard)]">
              <AlertTriangle className="h-3.5 w-3.5" />{warnCount} flagged
            </span>
          )}
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="py-10 text-center text-[var(--text-ter)]">
          <Package className="h-8 w-8 mx-auto mb-2 text-[var(--text-ter)]" />
          <p className="text-sm font-medium text-[var(--text-sec)]">No items yet</p>
          <p className="text-xs text-[var(--text-ter)] mt-1">Use "Add Items" above to populate this collection.</p>
        </div>
      ) : (
        <div className="divide-y divide-[var(--rule)]">
          {rows.map(({ item, scopeOutcomes, allOutcomes }) => {
            const displayOutcomes = activeTab === 'scope' ? scopeOutcomes : allOutcomes;
            const level = worstLevel(displayOutcomes);
            const chip = SCOPE_LEVEL_CHIP[level];
            const ChipIcon = chip.icon;
            const isExpanded = expandedId === item.id;
            const hasIssues = displayOutcomes.length > 0 && level !== 'allow';

            return (
              <div key={item.id} className="group">
                {/* Main row */}
                <div
                  className={`flex items-center gap-3 px-4 py-2.5 transition-colors ${
                    isExpanded ? 'bg-[var(--surface)]' : 'hover:bg-[var(--surface)]/60'
                  }`}
                >
                  {/* Expand toggle (only when there are issues) */}
                  <button
                    type="button"
                    onClick={() => hasIssues && setExpandedId(isExpanded ? null : item.id)}
                    className={`flex-shrink-0 p-0.5 rounded transition-colors ${
                      hasIssues ? 'text-[var(--text-ter)] hover:text-[var(--text-sec)] cursor-pointer' : 'text-transparent cursor-default'
                    }`}
                  >
                    {isExpanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                  </button>

                  {/* Title */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[var(--text-pri)] truncate">{item.title}</p>
                    {item.description && !isExpanded && (
                      <p className="text-xs text-[var(--text-ter)] truncate mt-0.5">{item.description}</p>
                    )}
                  </div>

                  {/* Category chip */}
                  {item.itemCategory && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-[var(--hover-tint)] text-[var(--text-sec)] capitalize whitespace-nowrap flex-shrink-0 hidden sm:inline-flex">
                      {item.itemCategory}
                    </span>
                  )}

                  {/* Scope status chip */}
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${chip.cls}`}>
                    <ChipIcon className="h-3 w-3" />
                    {chip.label}
                    {displayOutcomes.length > 0 && level !== 'allow' && (
                      <span className="opacity-70">({displayOutcomes.length})</span>
                    )}
                  </span>

                  <Link
                    to={`/prioritization/items/${item.id}?tab=relationships`}
                    className="p-1 text-[var(--text-ter)] hover:text-primary-600 flex-shrink-0 transition-colors"
                    title="View relationships"
                  >
                    <GitBranch className="h-3.5 w-3.5" />
                  </Link>

                  {/* View item link */}
                  <Link
                    to={`/prioritization/items/${item.id}`}
                    className="p-1 text-[var(--text-ter)] hover:text-primary-600 flex-shrink-0 transition-colors"
                    title="View item"
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                  </Link>

                  {/* Remove */}
                  <button
                    onClick={() => onRemoveItem(item.id)}
                    className="p-1 text-[var(--text-ter)] hover:text-[var(--coral)] flex-shrink-0 transition-colors"
                    title="Remove from collection"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>

                {/* Expanded outcomes */}
                {isExpanded && displayOutcomes.length > 0 && (
                  <div className="px-10 pb-3 space-y-1.5">
                    {displayOutcomes.map((o, idx) => {
                      const oCfg = SCOPE_LEVEL_CHIP[o.level];
                      const OIcon = oCfg.icon;
                      return (
                        <div key={idx} className="flex items-start gap-2.5 bg-[var(--surface)] px-3 py-2">
                          <OIcon className={`h-3.5 w-3.5 flex-shrink-0 mt-0.5 ${
                            o.level === 'block' ? 'text-[var(--coral)]' :
                            o.level === 'require_approval' ? 'text-[var(--mustard)]' :
                            o.level === 'warn' ? 'text-[var(--mustard)]' : 'text-[var(--green)]'
                          }`} />
                          <div className="flex-1 min-w-0">
                            <p className="text-xs text-[var(--text-sec)]">{o.message}</p>
                            <p className="text-xs text-[var(--text-ter)] font-mono mt-0.5">
                              Gate {o.gate} · {o.reason}
                            </p>
                          </div>
                          <Link
                            to={`/prioritization/items/${item.id}?tab=relationships`}
                            className="text-xs text-primary-600 hover:underline flex-shrink-0"
                          >
                            Fix →
                          </Link>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Collection Detail Panel (right side)
// ============================================================================

function CollectionDetailPanel({
  collection,
  itemIds,
  onRename,
  onDelete,
  onAddItem,
  onRemoveItem,
}: {
  collection: Collection;
  itemIds: string[];
  onRename: (name: string) => void;
  onDelete: () => void;
  onAddItem: (itemId: string) => void;
  onRemoveItem: (itemId: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [showAddItems, setShowAddItems] = useState(false);

  const allItems = useItemsStore((s) => s.items);
  const allGroups = useItemRelationshipsStore((s) => s.groups);
  const allMemberships = useItemRelationshipsStore((s) => s.groupMemberships);
  const allEdges = useItemRelationshipsStore((s) => s.edges);

  const memberItems = useMemo(
    () => allItems.filter((i) => itemIds.includes(i.id)),
    [allItems, itemIds],
  );

  // Build per-item scope rows with gate outcomes
  const scopeRows = useMemo((): ItemScopeRow[] => {
    if (itemIds.length === 0) return [];
    const scopeSet = new Set(itemIds);
    return memberItems.map((item) => {
      const ctx: RelationshipEvalContext = {
        itemId: item.id,
        allGroups,
        allMemberships,
        allEdges,
        itemFrames: item.frames ?? [],
        scopeItemIds: scopeSet,
        activeFrameId: null,
        declaredTrackRole: null,
        selectedElaborationChildId: null,
        ringAtTime: 3,
        isCommitmentTime: false,
      };
      const result = evaluateItemRelationships(ctx);
      return {
        item,
        scopeOutcomes: result.outcomes.filter((o) => o.gate === 3),
        allOutcomes: result.outcomes,
      };
    });
  }, [memberItems, allGroups, allMemberships, allEdges, itemIds]);

  // Aggregate stats
  const blockedCount = scopeRows.filter((r) => worstLevel(r.scopeOutcomes) === 'block').length;
  const flaggedCount = scopeRows.filter((r) => ['warn', 'require_approval'].includes(worstLevel(r.scopeOutcomes))).length;
  const clearCount = scopeRows.filter((r) => worstLevel(r.scopeOutcomes) === 'allow').length;

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="bg-[var(--bg-raised)] border border-[var(--rule)] px-4 py-3 flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-0">
          {editing ? (
            <RenameInput
              value={collection.name}
              onSave={(v) => { if (v) onRename(v); setEditing(false); }}
              onCancel={() => setEditing(false)}
            />
          ) : (
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold text-[var(--text-pri)] truncate">{collection.name}</h3>
              <button
                onClick={() => setEditing(true)}
                className="p-1 text-[var(--text-ter)] hover:text-[var(--text-sec)] rounded transition-colors"
                title="Rename"
              >
                <Pencil className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
          {collection.description && !editing && (
            <p className="text-xs text-[var(--text-ter)] mt-0.5 truncate">{collection.description}</p>
          )}
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={() => setShowAddItems(!showAddItems)}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium  transition-colors ${
              showAddItems
                ? 'bg-primary-100 text-primary-700 border border-primary-200'
                : 'bg-primary-600 text-white hover:bg-primary-700'
            }`}
          >
            <Plus className="h-3.5 w-3.5" />
            Add Items
          </button>
          <button
            onClick={onDelete}
            className="p-2 text-[var(--text-ter)] hover:text-[var(--coral)] hover:bg-[var(--coral-dim)] transition-colors"
            title="Delete collection"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Stats strip */}
      <div className="bg-[var(--bg-raised)] border border-[var(--rule)] overflow-hidden">
        <div className="grid grid-cols-4 divide-x divide-[var(--rule)]">
          <div className="px-4 py-3">
            <p className="text-xs text-[var(--text-ter)]">Items</p>
            <p className="text-base font-semibold text-[var(--text-pri)] tabular-nums">{itemIds.length}</p>
          </div>
          <div className="px-4 py-3">
            <p className="text-xs text-[var(--text-ter)]">Blocked</p>
            <p className={`text-base font-semibold tabular-nums ${blockedCount > 0 ? 'text-[var(--coral)]' : 'text-[var(--text-ter)]'}`}>
              {blockedCount}
            </p>
          </div>
          <div className="px-4 py-3">
            <p className="text-xs text-[var(--text-ter)]">Flagged</p>
            <p className={`text-base font-semibold tabular-nums ${flaggedCount > 0 ? 'text-[var(--mustard)]' : 'text-[var(--text-ter)]'}`}>
              {flaggedCount}
            </p>
          </div>
          <div className="px-4 py-3">
            <p className="text-xs text-[var(--text-ter)]">Clear</p>
            <p className={`text-base font-semibold tabular-nums ${clearCount > 0 ? 'text-[var(--green)] dark:text-[var(--text-ter)]' : 'text-[var(--text-ter)]'}`}>
              {clearCount}
            </p>
          </div>
        </div>
      </div>

      {/* Add Items panel */}
      {showAddItems && (
        <AddItemsPanel
          existingIds={itemIds}
          onAdd={(id) => { onAddItem(id); }}
          onClose={() => setShowAddItems(false)}
        />
      )}

      {/* Scope Analyzer table — replaces the old flat banner + items list */}
      <ScopeAnalyzerTable
        rows={scopeRows}
        onRemoveItem={onRemoveItem}
      />
    </div>
  );
}

// ============================================================================
// Main Page
// ============================================================================

export default function CycleDesignCollectionsPage() {
  const collections = useCollectionsStore((s) => s.collections);
  const memberships = useCollectionsStore((s) => s.memberships);
  const fetchCollections = useCollectionsStore((s) => s.fetchFromSupabase);
  const createCollection = useCollectionsStore((s) => s.createCollection);
  const updateCollection = useCollectionsStore((s) => s.updateCollection);
  const deleteCollection = useCollectionsStore((s) => s.deleteCollection);
  const addItemToCollection = useCollectionsStore((s) => s.addItemToCollection);
  const removeItemFromCollection = useCollectionsStore((s) => s.removeItemFromCollection);
  const fetchItems = useItemsStore((s) => s.fetchFromSupabase);
  const addNotification = useUIStore((s) => s.addNotification);

  const [activeCollectionId, setActiveCollectionId] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [pendingDeleteCollection, setPendingDeleteCollection] = useState<Collection | null>(null);

  useEffect(() => {
    fetchCollections();
    fetchItems();
  }, [fetchCollections, fetchItems]);

  // Auto-select first collection
  const hasAutoSelectedRef = useRef(false);
  useEffect(() => {
    if (collections.length === 0) { hasAutoSelectedRef.current = false; return; }
    if (activeCollectionId) return;
    if (hasAutoSelectedRef.current) return;
    hasAutoSelectedRef.current = true;
    setActiveCollectionId(collections[0].id);
  }, [activeCollectionId, collections]);

  const activeCollection = collections.find((c) => c.id === activeCollectionId) ?? null;

  const itemIdsByCollection = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const c of collections) {
      map.set(c.id, memberships.filter((m) => m.collectionId === c.id).map((m) => m.itemId));
    }
    return map;
  }, [collections, memberships]);

  const handleDelete = (collection: Collection) => {
    setPendingDeleteCollection(collection);
  };

  const confirmDelete = async () => {
    if (!pendingDeleteCollection) return;
    const collection = pendingDeleteCollection;
    setPendingDeleteCollection(null);
    await deleteCollection(collection.id);
    if (activeCollectionId === collection.id) {
      const remaining = collections.filter((c) => c.id !== collection.id);
      setActiveCollectionId(remaining.length > 0 ? remaining[0].id : null);
    }
    addNotification({ type: 'success', title: 'Collection deleted', message: `"${collection.name}" removed`, duration: 3000 });
  };

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<FolderOpen className="w-4 h-4 text-[var(--text-ter)]" />}
        iconBg="bg-[var(--slate-dim)]"
        title="Collections"
        subtitle="Stage candidate items into named groups for use in cycle design"
        actions={
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            New Collection
          </button>
        }
      />

      {/* Two-column editor layout — mirrors ItemPoolEditor */}
      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6">
        {/* Left: collection list */}
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] overflow-hidden lg:max-h-[calc(100vh-12rem)] flex flex-col">
          <div className="px-4 py-3 border-b border-[var(--rule)]">
            <h2 className="text-sm font-semibold text-[var(--text-pri)]">Collections</h2>
          </div>

          {collections.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center py-8 text-center px-4">
              <FolderOpen className="h-8 w-8 text-[var(--text-ter)] mb-2" />
              <p className="text-sm font-medium text-[var(--text-sec)]">No collections yet</p>
              <p className="text-xs text-[var(--text-ter)] mt-1">Create one to group related items.</p>
              <button
                onClick={() => setShowCreateModal(true)}
                className="mt-3 inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-primary-700 bg-primary-50 border border-primary-200 hover:bg-primary-100 transition-colors"
              >
                <Plus className="h-3.5 w-3.5" /> New Collection
              </button>
            </div>
          ) : (
            <div className="flex-1 min-h-0 overflow-y-auto divide-y divide-[var(--rule)]">
              {collections.map((collection) => {
                const isActive = collection.id === activeCollectionId;
                const count = itemIdsByCollection.get(collection.id)?.length ?? 0;
                return (
                  <button
                    key={collection.id}
                    type="button"
                    onClick={() => setActiveCollectionId(collection.id)}
                    className={`w-full text-left px-4 py-3 transition-colors ${
                      isActive ? 'bg-primary-50' : 'hover:bg-[var(--surface)]'
                    }`}
                  >
                    <div className={`font-medium text-sm truncate ${isActive ? 'text-primary-800' : 'text-[var(--text-pri)]'}`}>
                      {collection.name}
                    </div>
                    <div className="text-xs text-[var(--text-ter)] mt-0.5">
                      {count} {count === 1 ? 'item' : 'items'}
                    </div>
                    {collection.description && (
                      <div className="text-xs text-[var(--text-ter)] truncate mt-0.5">{collection.description}</div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Right: detail panel */}
        <div className="min-w-0">
          {!activeCollection ? (
            <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-12 text-center">
              <FolderOpen className="h-8 w-8 mx-auto mb-2 text-[var(--text-ter)]" />
              <p className="text-sm font-medium text-[var(--text-sec)]">No collection selected</p>
              <p className="text-xs text-[var(--text-ter)] mt-1">Select a collection from the list, or create one.</p>
            </div>
          ) : (
            <CollectionDetailPanel
              collection={activeCollection}
              itemIds={itemIdsByCollection.get(activeCollection.id) ?? []}
              onRename={(name) => updateCollection(activeCollection.id, { name })}
              onDelete={() => handleDelete(activeCollection)}
              onAddItem={(itemId) => addItemToCollection(activeCollection.id, itemId)}
              onRemoveItem={(itemId) => removeItemFromCollection(activeCollection.id, itemId)}
            />
          )}
        </div>
      </div>

      <CreateCollectionModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreate={async (name, description) => {
          const created = await createCollection(name, description);
          if (created) {
            setActiveCollectionId(created.id);
            addNotification({ type: 'success', title: 'Collection created', message: `"${name}" is ready`, duration: 3000 });
          }
        }}
      />

      {/* In-app delete confirmation */}
      {pendingDeleteCollection && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-[var(--bg-raised)] shadow-xl w-full max-w-sm mx-4 p-6">
            <h3 className="text-base font-semibold text-[var(--text-pri)] mb-2">Delete collection?</h3>
            <p className="text-sm text-[var(--text-sec)] mb-5">
              <span className="font-medium">"{pendingDeleteCollection.name}"</span> will be removed. Items inside it will not be deleted.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setPendingDeleteCollection(null)}
                className="px-4 py-2 text-sm text-[var(--text-sec)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)] transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 text-sm font-medium bg-[var(--coral)] text-white hover:bg-[var(--coral)] transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
